import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import TextField from '../textfield/textfield'
import getTimeFormatter from './get-time-formatter'

import styles from './time-selector.module.css'

const propTypes = {
    allowEmpty: PropTypes.bool,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.instanceOf(Error),
    onChange: PropTypes.func,
    onKeyUp: PropTypes.func,
    placeholder: PropTypes.string,
    toggleClassName: PropTypes.string,
    value: PropTypes.string.isRequired
}

const defaultProps = {
    allowEmpty: false,
    disabled: false,
    onChange: Function.prototype,
    onKeyUp: Function.prototype,
    placeholder: 'hh:mm'
}

/**
 * TextField for time input. Shows a AM/PM toggle on the right if the current
 * locale uses the 12h clock.
 *
 * @author Andy Wermke
 */
class TimeSelector extends Component {
    constructor(props) {
        super(props)

        this.onBlurChangeString = this.onBlurChangeString.bind(this)
        this.onChangeAMPM = this.onChangeAMPM.bind(this)
        this.onKeyUp = this.onKeyUp.bind(this)

        this.state = {
            amPm: '',
            onFormat() {},
            timeWithoutAmPm: ''
        }
    }

    UNSAFE_componentWillMount() {
        const { allowEmpty, value } = this.props
        const { amPm, onFormat, timeWithoutAmPm } = this.formatTimeHelper(value, allowEmpty)
        this.setState({ amPm, onFormat, timeWithoutAmPm })
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { value: currentValue } = this.props
        const { allowEmpty, value: nextValue } = nextProps
        // Just update the value if the initial value is not the same as the new value.
        if (currentValue !== nextValue) {
            const { amPm, onFormat, timeWithoutAmPm } = this.formatTimeHelper(nextValue, allowEmpty)
            this.setState({ amPm, onFormat, timeWithoutAmPm })
        }
    }

    render() {
        const { className, containerClassName, disabled, error, placeholder, toggleClassName } = this.props
        const { amPm, onFormat, timeWithoutAmPm } = this.state

        return (
            <TextField
                ref={ref => {
                    this.textFieldRef = ref
                }}
                containerClassName={cx('t3sel-time-selector', styles['time-selector'], containerClassName)}
                value={timeWithoutAmPm}
                onBlur={this.onBlurChangeString}
                onKeyUp={this.onKeyUp}
                onChange={event => {
                    if (this.textFieldRef) {
                        this.setState({
                            timeWithoutAmPm: event.target.value
                        })
                    }
                }}
                {...{ className, disabled, error, onFormat, placeholder }}
                maxLength={5}
                rightIcon={
                    amPm && (
                        <ToggleAMPM
                            {...{ disabled }}
                            isPM={amPm === 'PM'}
                            onToggle={this.onChangeAMPM}
                            className={toggleClassName}
                        />
                    )
                }
            />
        )
    }

    /**
     * Invert the given AM to PM and vice versa.
     * @param {string} amPm - The AM or PM string
     * @returns {string} The inverted AM or PM string
     */
    invertAMPM(amPm) {
        return amPm === 'PM' ? 'AM' : 'PM'
    }

    /**
     * Add the given PM or AM string to the given time if available.
     * @param {string} timeFormatted - The formatted time string
     * @param {string} [amPm] - The given AM or PM string
     * @returns {string}
     */
    formatTime(timeFormatted, amPm) {
        return `${timeFormatted}${amPm ? ` ${amPm}` : ''}`
    }

    /**
     * @typedef {Object} formatTimeHelperObject
     * @property {string|null} amPm The AM or PM string of the given time - null if not available at the times string
     * @property {Function} onFormat The correct format function by 12 or 24 h
     * @property {string} timeWithoutAmPm The blank time string without AM or PM prefix
     */

    /**
     * This is a helper to reduce duplicated code.
     * This helper identifies the time and the AM or PM string and split it up.
     * It also gets the correct time format function depending by 12 or 24 h.
     *
     * @param {string} value - The given time string
     * @param {boolean} allowEmpty - Allow time without AM or PM string
     * @returns {formatTimeHelperObject}
     */
    formatTimeHelper(value, allowEmpty) {
        // Split `value` into `timeWithoutAmPm` & `amPm`
        const match = value.match(/^([^\s]*)(\s(AM|PM))?$/i)
        let timeWithoutAmPm = match ? match[1] : value
        let amPm = match ? match[3] : null

        if (allowEmpty && !amPm) {
            const emptyMatch = value.match(/^[\s]*?(AM|PM)$/i)
            amPm = emptyMatch ? emptyMatch[1] : null
            timeWithoutAmPm = emptyMatch ? '' : timeWithoutAmPm
        }

        const onFormat = getTimeFormatter(!!amPm, allowEmpty)

        return {
            amPm: amPm && amPm.toUpperCase(),
            onFormat,
            timeWithoutAmPm
        }
    }

    /**
     *
     * @param event
     */
    onBlurChangeString(event) {
        const { onChange } = this.props
        const { amPm, onFormat } = this.state

        this.setState({ timeWithoutAmPm: event.target.value })
        return onChange(this.formatTime(onFormat(event.target.value, true), amPm))
    }

    onKeyUp(event) {
        this.props.onKeyUp(event)
    }

    /**
     * Invert the am to pm and vice versa.
     */
    onChangeAMPM() {
        const { disabled, onChange } = this.props
        const { amPm, onFormat, timeWithoutAmPm } = this.state

        if (!disabled) {
            const invertedAmPm = this.invertAMPM(amPm)
            this.setState({ amPm: invertedAmPm })
            return onChange(this.formatTime(onFormat(timeWithoutAmPm, true), invertedAmPm))
        }
    }
}

TimeSelector.propTypes = propTypes
TimeSelector.defaultProps = defaultProps

const toggleAMPMPropTypes = {
    className: PropTypes.string,
    disabled: PropTypes.bool,
    isPM: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired
}

function ToggleAMPM({ className, disabled, isPM, onToggle }) {
    className = cx('t3sel-time-ampm', styles['am-pm-toggle'], {
        [styles['am-pm-toggle-disabled']]: disabled,
        [styles['am-pm-toggle-enabled']]: !disabled,
        className
    })
    const label = isPM ? 'PM' : 'AM'

    return (
        <div className={className} role="button" onClick={onToggle}>
            <span className={styles['am-pm-toggle-label']}>{label}</span>
        </div>
    )
}

ToggleAMPM.propTypes = toggleAMPMPropTypes

export default TimeSelector
